// Portfolio Administration v1.0 PR 6 — paginated table of cross-portfolio
// LoanTapeEntries for the selected period. Each row links to the per-loan
// Payment Tracking tab.
//
// WS-4 legacy-tape retirement: rows are read-only historical data — the
// selection checkboxes and per-row entry/variance actions are gone.

import { Link } from "react-router-dom";
import { Badge } from "@/design-system/components/ui";
import type { LoanTapeEntryDto } from "@/api/portfolioAdmin/pr6Types";

function fmtCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const STATUS_VARIANT: Record<string, "draft" | "review" | "finalized" | "archived" | "info" | "gold"> = {
  open: "draft",
  entered: "info",
  "variance-pending": "review",
  locked: "finalized",
  distributed: "gold",
  superseded: "archived",
};

interface LoanTapeTableProps {
  entries: LoanTapeEntryDto[];
  page: number;
  pageSize: number;
  onPageChange: (next: number) => void;
}

export function LoanTapeTable({
  entries,
  page,
  pageSize,
  onPageChange,
}: LoanTapeTableProps) {
  const start = page * pageSize;
  const end = Math.min(start + pageSize, entries.length);
  const pageEntries = entries.slice(start, end);
  const totalPages = Math.max(1, Math.ceil(entries.length / pageSize));

  if (entries.length === 0) {
    return (
      <p
        className="text-small text-gray-mid italic m-0"
        data-testid="loan-tape-empty"
      >
        No Loan Tape entries match the current filters.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-space-2">
      <div className="overflow-x-auto" data-testid="loan-tape-table">
        <table className="w-full border-collapse">
          <thead className="text-caption text-gray-mid uppercase">
            <tr className="border-b border-border-light">
              <th className="px-space-2 py-space-1 text-left">Loan</th>
              <th className="px-space-2 py-space-1 text-right">Scheduled</th>
              <th className="px-space-2 py-space-1 text-right">Received</th>
              <th className="px-space-2 py-space-1 text-left">Status</th>
              <th className="px-space-2 py-space-1 text-right">Days late</th>
              <th className="px-space-2 py-space-1 text-left">Waterfall run</th>
              <th className="px-space-2 py-space-1 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageEntries.map((e) => {
              return (
                <tr
                  key={e.id}
                  className="border-b border-border-light hover:bg-white-warm"
                  data-testid={`loan-tape-row-${e.id}`}
                >
                  <td className="px-space-2 py-space-1 text-small">
                    <Link
                      to={`/portfolio-admin/loans/${e.loanId}?tab=payment-tracking`}
                      className="text-gold-shadow hover:underline font-mono"
                    >
                      {e.loanId.slice(0, 8).toUpperCase()}
                    </Link>
                    {e.varianceFlag && (
                      <span
                        className="ml-space-1 text-status-warning"
                        title={e.varianceReason ?? "Variance flagged"}
                        data-testid={`loan-tape-row-${e.id}-variance`}
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="px-space-2 py-space-1 text-small text-right">
                    {fmtCurrency(e.scheduledPaymentAmount)}
                  </td>
                  <td className="px-space-2 py-space-1 text-small text-right">
                    {fmtCurrency(e.paymentReceivedAmount)}
                  </td>
                  <td className="px-space-2 py-space-1">
                    <Badge status={STATUS_VARIANT[e.status] ?? "draft"}>
                      {e.status}
                    </Badge>
                  </td>
                  <td className="px-space-2 py-space-1 text-small text-right">
                    {e.daysLate}
                  </td>
                  <td className="px-space-2 py-space-1 text-small font-mono">
                    {e.primaryWaterfallRunId
                      ? e.primaryWaterfallRunId.slice(0, 8).toUpperCase()
                      : "—"}
                  </td>
                  <td className="px-space-2 py-space-1 text-small">
                    <Link
                      to={`/portfolio-admin/loans/${e.loanId}?tab=payment-tracking`}
                      className="text-gold-shadow hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-caption text-gray-mid">
        <span data-testid="loan-tape-pagination-summary">
          Showing {entries.length === 0 ? 0 : start + 1}-{end} of {entries.length}
        </span>
        <div className="flex items-center gap-space-1">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page === 0}
            className="px-space-2 py-space-1 border border-border-light rounded-sm disabled:opacity-40"
            data-testid="loan-tape-page-prev"
          >
            Prev
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className="px-space-2 py-space-1 border border-border-light rounded-sm disabled:opacity-40"
            data-testid="loan-tape-page-next"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
